<template>
  <transition name="incoming-call">
    <div
      v-if="call"
      class="fixed top-5 left-5 z-[10000] w-[360px] max-w-[calc(100vw-2.5rem)] rounded-3xl border border-white/15 bg-[#1A1430]/95 p-[18px] pt-3.5 text-white shadow-2xl backdrop-blur cursor-pointer"
      dir="rtl"
      role="alert"
      @click="openCustomer"
    >
      <div class="flex items-center gap-2">
        <span class="relative flex h-2 w-2">
          <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span class="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
        </span>
        <span class="flex-1 truncate text-xs font-medium text-violet-300">שיחה נכנסת בטלפון · לקוח/ה</span>
        <button
          type="button"
          class="flex h-7 w-7 items-center justify-center rounded-full bg-white/10 text-white/70 hover:bg-white/20"
          aria-label="סגירה"
          @click.stop="dismissIncomingCall"
        >
          <i class="pi pi-times text-[11px]" />
        </button>
      </div>

      <div class="mt-2.5 flex items-center gap-3.5">
        <div class="flex h-[52px] w-[52px] flex-none items-center justify-center rounded-full bg-gradient-to-br from-violet-400 to-[#7048E8] text-lg font-semibold">
          {{ initials }}
        </div>
        <div class="min-w-0 flex-1">
          <div class="truncate text-[21px] font-semibold leading-tight">{{ call.fullName }}</div>
          <div v-if="subtitle" class="mt-0.5 truncate text-[13px] text-white/60">{{ subtitle }}</div>
        </div>
        <span
          v-if="call.accountBalance"
          class="flex-none rounded-full px-2.5 py-1 text-xs font-medium"
          :class="call.accountBalance < 0 ? 'bg-red-400/20 text-red-300' : 'bg-emerald-400/20 text-emerald-300'"
        >{{ call.accountBalance < 0 ? 'חוב' : 'יתרה' }} ₪{{ Math.abs(call.accountBalance) }}</span>
      </div>

      <div class="mt-3.5 rounded-2xl bg-white/[0.08] px-3.5 pb-3 pt-2.5">
        <div class="text-[11px] text-white/50">התור הבא</div>
        <div class="mt-0.5 text-base font-semibold" :class="{ 'text-white/60': !call.nextAppointment }">
          {{ call.nextAppointment?.display ?? 'אין תור עתידי' }}
        </div>
        <div v-if="call.nextAppointment?.services" class="mt-px truncate text-[13px] text-violet-300">
          {{ call.nextAppointment.services }}
        </div>
      </div>

      <div v-if="call.warningNote" class="mt-2.5 rounded-xl bg-amber-400/15 px-3 py-2 text-[13px] text-amber-300">
        ⚠️ {{ call.warningNote }}
      </div>

      <button
        type="button"
        class="mt-3.5 h-11 w-full rounded-[14px] bg-primary text-[15px] font-medium text-white hover:brightness-110"
        @click.stop="openCustomer"
      >
        פתיחת כרטיס לקוח
      </button>
    </div>
  </transition>
</template>

<script setup lang="ts">
import {computed} from 'vue';
import {useRouter} from 'vue-router';

import {AdminRouteNames} from '@app/router/route-names';
import {trackEvent} from '@app/composables/posthog';
import {dismissIncomingCall, incomingCall} from '@app/composables/incoming-call-live';

const router = useRouter();
const call = incomingCall;

const initials = computed(() =>
  (call.value?.fullName ?? '').split(/\s+/).filter(w => w && w !== '/').slice(0, 2).map(w => w[0]).join('')
);
const subtitle = computed(() => {
  const parts: string[] = [];
  if (call.value?.lastVisit) parts.push(`ביקור אחרון ${call.value.lastVisit}`);
  if (call.value?.tags?.length) parts.push(call.value.tags.join(' · '));
  return parts.join(' · ');
});

const openCustomer = () => {
  if (!call.value) return;
  trackEvent('caller_id_desktop_card_opened');
  router.push({name: AdminRouteNames.ClientProfile, params: {id: call.value.customerId}});
  dismissIncomingCall();
};
</script>

<style scoped>
.incoming-call-enter-active,
.incoming-call-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.incoming-call-enter-from,
.incoming-call-leave-to {
  opacity: 0;
  transform: translateY(-16px);
}
</style>
